import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from 'react';
import classNames from 'classnames';

import { SideMenu } from 'components/shared/side-menu/index';
import {
  SIDEMENU_COLLAPSED_KEY,
  SIDEMENU_WIDTH,
} from 'components/shared/side-menu/side-menu.constants';
import ImpersonationBanner from 'components/shared/others/impersonation-banner/impersonation-banner';

import { useImpersonation } from 'hooks/use-impersonation';

interface SideMenuContextValue {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
}

export const SideMenuContext = createContext<SideMenuContextValue>({
  isCollapsed: false,
  toggleCollapsed: () => {},
});

export const useSideMenuContext = () => useContext(SideMenuContext);

interface SideMenuLayoutProps {
  children?: React.ReactNode;
}

const SideMenuLayout = ({ children }: SideMenuLayoutProps) => {
  const { impersonatingUser, isImpersonating } = useImpersonation();

  const hasImpersonation = impersonatingUser || isImpersonating();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEMENU_COLLAPSED_KEY) === 'true';
    } catch (e) {
      console.warn('Failed to read sidemenu state from localStorage:', e);

      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const newState = !prev;

      try {
        localStorage.setItem(SIDEMENU_COLLAPSED_KEY, String(newState));
      } catch (e) {
        console.warn('Failed to save sidemenu state to localStorage:', e);
      }

      return newState;
    });
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SIDEMENU_COLLAPSED_KEY) {
        try {
          setIsCollapsed(e.newValue === 'true');
        } catch (err) {
          console.warn('Failed to handle storage change:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const contentMargin = isCollapsed
    ? SIDEMENU_WIDTH.collapsedMargin
    : SIDEMENU_WIDTH.expandedMargin;
  const bannerLeft = isCollapsed
    ? SIDEMENU_WIDTH.collapsedLeft
    : SIDEMENU_WIDTH.expandedLeft;

  return (
    <SideMenuContext.Provider value={{ isCollapsed, toggleCollapsed }}>
      <div className="flex min-h-screen">
        <SideMenu />

        {hasImpersonation && (
          <div
            className={classNames(
              'fixed top-0 right-0 z-[90] transition-all duration-300',
              bannerLeft
            )}
          >
            <ImpersonationBanner />
          </div>
        )}

        <div
          className={classNames(
            'flex-1 flex flex-col transition-all duration-300',
            contentMargin,
            { 'pt-12': hasImpersonation }
          )}
        >
          {children}
        </div>
      </div>
    </SideMenuContext.Provider>
  );
};

export default SideMenuLayout;
